import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Users, ArrowLeft, Check, UserPlus, X, Search, ExternalLink } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const APP_STORE_URL = 'https://apps.apple.com/it/app/ananda-sporting-club/id6502181699';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=it.anytimes.anandasportingclub.app';

const CreateMatchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(2);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showBookingReminder, setShowBookingReminder] = useState(false);
  const [createdMatchId, setCreatedMatchId] = useState(null);
  const [matchType, setMatchType] = useState('amichevole'); // "amichevole" or "classifica"

  // Player invitation
  const [players, setPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const [opponentCounts, setOpponentCounts] = useState({}); // {user_id: {count, can_challenge, is_inferior, inferior_used}} for classifica limit

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    if (matchType === 'classifica') {
      fetchOpponentCounts();
    }
  }, [matchType]);

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setPlayers(res.data.filter(p => p.user_id !== user?.user_id));
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const fetchOpponentCounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ranking/dynamic/eligible-opponents`, { withCredentials: true });
      const counts = {};
      res.data.forEach(o => {
        counts[o.user_id] = {
          count: o.matches_this_season || 0,
          can_challenge: o.can_challenge,
          is_inferior: o.is_inferior || false,
          inferior_used: o.inferior_challenges_used,
        };
      });
      setOpponentCounts(counts);
    } catch (error) {
      console.error('Error fetching opponent counts:', error);
    }
  };

  const filteredPlayers = players.filter(p => {
    const query = searchQuery.toLowerCase();
    const fullName = `${p.name || ''} ${p.surname || ''}`.toLowerCase();
    return fullName.includes(query) && !selectedPlayers.some(s => s.user_id === p.user_id);
  });

  const addPlayer = (player) => {
    if (selectedPlayers.length >= maxPlayers - 1) {
      toast.error(`Puoi invitare al massimo ${maxPlayers - 1} giocator${maxPlayers - 1 === 1 ? 'e' : 'i'}`);
      return;
    }
    const info = opponentCounts[player.user_id];
    if (matchType === 'classifica' && info && !info.can_challenge) {
      if (info.is_inferior && info.inferior_used >= 5) {
        toast.error('Hai raggiunto il limite di 5 sfide contro fascia inferiore questa stagione');
      } else {
        toast.error(`Hai già giocato 2 sfide di classifica contro ${player.name} in questa stagione`);
      }
      return;
    }
    setSelectedPlayers(prev => [...prev, player]);
    setSearchQuery('');
  };

  const removePlayer = (userId) => {
    setSelectedPlayers(prev => prev.filter(p => p.user_id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) { toast.error('Seleziona una data'); return; }
    if (!time) { toast.error('Seleziona un orario'); return; }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/matches`,
        {
          date: format(date, 'yyyy-MM-dd'),
          time: time,
          max_players: maxPlayers,
          invited_players: selectedPlayers.map(p => p.user_id),
          match_type: matchType
        },
        { withCredentials: true }
      );

      toast.success('Partita creata con successo!');
      if (selectedPlayers.length > 0) {
        toast.success(`Invito inviato a ${selectedPlayers.length} giocator${selectedPlayers.length === 1 ? 'e' : 'i'}!`);
      }
      setCreatedMatchId(response.data.match_id);
      setShowBookingReminder(true);
    } catch (error) {
      console.error('Error creating match:', error);
      toast.error('Errore nella creazione della partita');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00',
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="create-match-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-outfit text-2xl font-semibold text-slate-900">
          Nuova Partita
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Match Type Selector */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">Tipo di Partita</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMatchType('amichevole')}
              data-testid="match-type-amichevole"
              className={`p-4 rounded-xl border-2 text-center transition-all ${
                matchType === 'amichevole'
                  ? 'border-sky-400 bg-sky-50'
                  : 'border-slate-100 bg-slate-50 hover:border-slate-200'
              }`}
            >
              <span className="text-2xl block mb-1">&#127934;</span>
              <span className={`text-sm font-semibold ${matchType === 'amichevole' ? 'text-sky-700' : 'text-slate-600'}`}>Amichevole</span>
              <span className="block text-xs text-slate-400 mt-0.5">Per divertimento</span>
            </button>
            <button
              type="button"
              onClick={() => { setMatchType('classifica'); setMaxPlayers(2); }}
              data-testid="match-type-classifica"
              className={`p-4 rounded-xl border-2 text-center transition-all ${
                matchType === 'classifica'
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-slate-100 bg-slate-50 hover:border-slate-200'
              }`}
            >
              <span className="text-2xl block mb-1">&#127942;</span>
              <span className={`text-sm font-semibold ${matchType === 'classifica' ? 'text-amber-700' : 'text-slate-600'}`}>Sfida Classifica</span>
              <span className="block text-xs text-slate-400 mt-0.5">Influisce sul ranking</span>
            </button>
          </div>
          {matchType === 'classifica' && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2 mt-3">
              Le sfide di classifica sono 1 vs 1. Il risultato aggiorna automaticamente il ranking.
            </p>
          )}
        </div>

        {/* Info prenotazione campo */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200" data-testid="court-booking-info-create">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mt-0.5">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800">Prenotazione Campo</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Ricorda di prenotare il campo presso la struttura Ananda Sporting Club tramite l'app <strong>Prenotazione Campi</strong>.
              </p>
            </div>
          </div>
          <div className="flex gap-3 mt-3 ml-11">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-store-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              App Store
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="play-store-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm.91-.91L19.59 12l-1.87-2.21-2.27 2.27 2.27 2.15zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/></svg>
              Google Play
            </a>
          </div>
        </div>

        {/* Date Selection */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Calendar className="w-4 h-4 inline mr-2" />
            Data
          </label>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="date-picker-trigger"
                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-left hover:bg-white hover:border-sky-300 transition-all focus:outline-none focus:ring-2 focus:ring-sky-400/20"
              >
                {date ? (
                  <span className="text-slate-900 capitalize">
                    {format(date, 'EEEE d MMMM yyyy', { locale: it })}
                  </span>
                ) : (
                  <span className="text-slate-400">Seleziona una data</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={date}
                onSelect={(newDate) => {
                  setDate(newDate);
                  setCalendarOpen(false);
                }}
                disabled={(date) => date < new Date()}
                locale={it}
                className="rounded-2xl border-0"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time Selection */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Clock className="w-4 h-4 inline mr-2" />
            Orario
          </label>
          
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setTime(slot)}
                data-testid={`time-slot-${slot}`}
                className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  time === slot
                    ? 'bg-sky-400 text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {slot}
              </button>
            ))}
          </div>
        </div>

        {/* Max Players */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Users className="w-4 h-4 inline mr-2" />
            Numero Giocatori
          </label>
          
          <div className="flex gap-3">
            {[2, 4].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => {
                  setMaxPlayers(num);
                  // Remove excess selected players if reducing
                  if (selectedPlayers.length >= num) {
                    setSelectedPlayers(prev => prev.slice(0, num - 1));
                  }
                }}
                data-testid={`players-${num}`}
                className={`flex-1 px-4 py-4 rounded-2xl text-center transition-all ${
                  maxPlayers === num
                    ? 'bg-sky-400 text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="block text-2xl font-semibold">{num}</span>
                <span className="text-sm opacity-80">
                  {num === 2 ? 'Singolo' : 'Doppio'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Invite Players (Optional) */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-slate-700">
              <UserPlus className="w-4 h-4 inline mr-2" />
              Invita Giocatori <span className="text-slate-400 font-normal">(opzionale)</span>
            </label>
            <span className="text-xs text-slate-400">
              {selectedPlayers.length}/{maxPlayers - 1} selezionati
            </span>
          </div>

          {/* Selected Players */}
          {selectedPlayers.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedPlayers.map(p => (
                <div
                  key={p.user_id}
                  data-testid={`selected-player-${p.user_id}`}
                  className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-full pl-1 pr-3 py-1"
                >
                  <div className="w-7 h-7 bg-sky-400 rounded-full flex items-center justify-center text-white text-xs font-medium overflow-hidden">
                    {getAvatarUrl(p) ? (
                      <img src={getAvatarUrl(p)} alt={p.name} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      p.name?.charAt(0)
                    )}
                  </div>
                  <span className="text-sm font-medium text-sky-700">{p.name}</span>
                  <button
                    type="button"
                    onClick={() => removePlayer(p.user_id)}
                    className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-sky-200 transition-colors text-sky-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search Toggle */}
          {!showPlayerSearch ? (
            <button
              type="button"
              onClick={() => setShowPlayerSearch(true)}
              data-testid="show-player-search-btn"
              className="w-full py-3 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 hover:border-sky-300 hover:text-sky-500 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <UserPlus className="w-4 h-4" />
              {selectedPlayers.length === 0 ? 'Cerca e invita giocatori' : 'Invita un altro giocatore'}
            </button>
          ) : (
            <div>
              {/* Search Input */}
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="player-search-input"
                  placeholder="Cerca per nome..."
                  autoFocus
                  className="w-full pl-10 pr-10 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => { setShowPlayerSearch(false); setSearchQuery(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Player List */}
              <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl">
                {filteredPlayers.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {searchQuery ? 'Nessun giocatore trovato' : 'Nessun altro giocatore disponibile'}
                  </p>
                ) : (
                  filteredPlayers.slice(0, 10).map(p => {
                    const info = opponentCounts[p.user_id];
                    const atLimit = matchType === 'classifica' && info && !info.can_challenge;
                    const limitReason = atLimit && info?.is_inferior && info?.inferior_used >= 5
                      ? `Limite 5 sfide vs fascia inferiore raggiunto`
                      : atLimit ? 'Limite 2 sfide raggiunto questa stagione' : null;
                    return (
                    <button
                      key={p.user_id}
                      type="button"
                      onClick={() => addPlayer(p)}
                      disabled={atLimit}
                      data-testid={`invite-player-${p.user_id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${atLimit ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'hover:bg-sky-50'}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium overflow-hidden flex-shrink-0 ${atLimit ? 'bg-slate-300' : 'bg-sky-400'}`}>
                        {getAvatarUrl(p) ? (
                          <img src={getAvatarUrl(p)} alt={p.name} className="w-full h-full object-cover rounded-full" />
                        ) : (
                          p.name?.charAt(0)
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {p.name} {p.surname || ''}
                        </p>
                        {atLimit ? (
                          <p className="text-xs text-red-500">{limitReason}</p>
                        ) : (
                          <p className="text-xs text-slate-400">
                            Livello {p.level || 1}
                            {matchType === 'classifica' && info && info.count > 0 && (
                              <span className="ml-1.5 text-amber-500">({info.count}/2 sfide)</span>
                            )}
                          </p>
                        )}
                      </div>
                      {atLimit ? (
                        <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                      ) : (
                        <UserPlus className="w-4 h-4 text-sky-400 flex-shrink-0" />
                      )}
                    </button>
                  )})
                )}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !date || !time}
          data-testid="submit-match-btn"
          className="w-full py-4 bg-sky-400 text-white rounded-full font-medium text-lg hover:bg-sky-500 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              Crea Partita
            </>
          )}
        </button>
      </form>

      {/* Booking Reminder Overlay */}
      {showBookingReminder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="booking-reminder-overlay">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-fadeIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">
                Prenota il Campo!
              </h3>
              <p className="text-slate-500 text-sm mb-6">
                La partita è stata creata. Ricorda di prenotare il campo tramite l'app <strong>Prenotazione Campi</strong> di Ananda Sporting Club.
              </p>
              <div className="flex justify-center gap-3 mb-6">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reminder-app-store-link"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-black text-white text-xs font-medium rounded-xl hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  App Store
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reminder-play-store-link"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-black text-white text-xs font-medium rounded-xl hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm.91-.91L19.59 12l-1.87-2.21-2.27 2.27 2.27 2.15zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/></svg>
                  Google Play
                </a>
              </div>
              <button
                onClick={() => navigate(`/matches/${createdMatchId}`)}
                data-testid="booking-reminder-continue-btn"
                className="w-full py-3.5 bg-sky-400 text-white rounded-full font-medium hover:bg-sky-500 transition-all flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Ho capito, vai alla partita
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMatchPage;
